import React, { useState, useRef } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import "../styles/Drones.css"; // Import Drones.css instead

// GraphQL Definitions
const GET_SERVICES = gql`
  query {
    services {
      serviceId
      name
      specificDrone
      experience
      location
      description
      price
      image
      status
      sellerId
      sellerInfo {
        email
        phoneNumber
      }
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_SERVICE_STATUS = gql`
  mutation UpdateServiceStatus($serviceId: String!, $status: String!) {
    updateServiceStatus(serviceId: $serviceId, status: $status) {
      serviceId
      status
    }
  }
`;

const UPDATE_SERVICE = gql`
  mutation UpdateService($serviceId: String!, $name: String!, $specificDrone: String!, $experience: Float!, $location: String!, $price: Float!, $description: String!) {
    updateService(serviceId: $serviceId, name: $name, specificDrone: $specificDrone, experience: $experience, location: $location, price: $price, description: $description) {
      serviceId
      name
      specificDrone
      experience
      location
      price
      description
    }
  }
`;

const DELETE_SERVICE = gql`
  mutation DeleteService($serviceId: String!) {
    deleteService(serviceId: $serviceId) {
      serviceId
    }
  }
`;

function Services() {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [expandedSeller, setExpandedSeller] = useState(null);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "sellerId", direction: "asc" });
  const [viewingService, setViewingService] = useState(null);
  const [viewingDescription, setViewingDescription] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPage, setProductsPage] = useState({});
  const [sortOrder, setSortOrder] = useState("lifo");
  const ITEMS_PER_PAGE = 10;
  const PRODUCTS_PER_PAGE = 10;
  const tableRef = useRef(null);

  // Apollo Hooks
  const { loading, error: queryError, data, refetch } = useQuery(GET_SERVICES, {
    fetchPolicy: "network-only",
  });

  const [updateStatusMutation] = useMutation(UPDATE_SERVICE_STATUS);
  const [updateServiceMutation] = useMutation(UPDATE_SERVICE);
  const [deleteServiceMutation] = useMutation(DELETE_SERVICE);

  const services = data?.services || [];
  const error = queryError ? queryError.message : null;

  // Group services by seller ID
  const groupedBySeller = services.reduce((acc, service) => {
    const sellerId = service.sellerId;
    if (!acc[sellerId]) {
      acc[sellerId] = {
        sellerId: sellerId,
        sellerInfo: service.sellerInfo,
        services: []
      };
    }
    acc[sellerId].services.push(service);
    return acc;
  }, {});

  // Convert to array and sort
  const sellerGroups = Object.values(groupedBySeller).sort((a, b) =>
    a.sellerId.localeCompare(b.sellerId)
  );

  // Calculate seller stats
  const sellerGroupsWithStats = sellerGroups.map(seller => {
    const totalValue = seller.services.reduce((sum, s) => sum + s.price, 0);
    const totalExperience = seller.services.reduce((sum, s) => sum + s.experience, 0);
    const approvedServices = seller.services.filter(s => s.status === 'approved').length;
    const pendingServices = seller.services.filter(s => s.status === 'pending').length;
    const rejectedServices = seller.services.filter(s => s.status === 'rejected').length;
    const deactivatedServices = seller.services.filter(s => s.status === 'Temporarily_Deactivated').length;
    
    return {
      ...seller,
      totalValue,
      totalExperience,
      approvedServices,
      pendingServices,
      rejectedServices,
      deactivatedServices,
      totalServices: seller.services.length,
      avgExperience: seller.services.length > 0 ? (totalExperience / seller.services.length).toFixed(1) : 0
    };
  });

  // Filter sellers based on search
  const filteredSellers = sellerGroupsWithStats.filter(seller => {
    const matchesSearch =
      searchTerm === "" ||
      seller.sellerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.phoneNumber?.includes(searchTerm);
    
    if (selectedStatus === "all") return matchesSearch;
    
    // Filter by status
    const filteredServices = seller.services.filter(service => 
      service.status.toLowerCase() === selectedStatus
    );
    
    return matchesSearch && filteredServices.length > 0;
  });

  // Sort sellers
  const sortedSellers = [...filteredSellers].sort((a, b) => {
    const key = sortConfig.key;
    let aVal = a[key];
    let bVal = b[key];

    if (key === 'sellerId' || key === 'email') {
      aVal = aVal?.toLowerCase?.() || '';
      bVal = bVal?.toLowerCase?.() || '';
    }

    if (sortConfig.direction === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination for sellers
  const totalFilteredPages = Math.ceil(sortedSellers.length / ITEMS_PER_PAGE);
  const paginatedSellers = sortedSellers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Get filtered services for expanded seller with pagination
  const getFilteredServicesForSeller = (sellerId) => {
    const seller = groupedBySeller[sellerId];
    if (!seller) return [];
    
    return seller.services.filter(service => {
      return selectedStatus === "all" || service.status.toLowerCase() === selectedStatus;
    });
  };

  // Get paginated products for a seller
  const getPaginatedProductsForSeller = (sellerId) => {
    const filteredServices = getFilteredServicesForSeller(sellerId);
    const currentProductsPage = productsPage[sellerId] || 1;
    const startIndex = (currentProductsPage - 1) * PRODUCTS_PER_PAGE;
    const endIndex = startIndex + PRODUCTS_PER_PAGE;
    
    return {
      products: filteredServices.slice(startIndex, endIndex),
      currentPage: currentProductsPage,
      totalPages: Math.ceil(filteredServices.length / PRODUCTS_PER_PAGE),
      totalProducts: filteredServices.length
    };
  };

  // Handle products page change
  const handleProductsPageChange = (sellerId, page) => {
    setProductsPage(prev => ({
      ...prev,
      [sellerId]: page
    }));
  };

  // Update service status
  const updateServiceStatus = async (serviceId, newStatus) => {
    const updatingIdsCopy = new Set(updatingIds);
    updatingIdsCopy.add(`${serviceId}-${newStatus}`);
    setUpdatingIds(updatingIdsCopy);

    try {
      await updateStatusMutation({
        variables: { serviceId, status: newStatus.toLowerCase() },
      });
      refetch();
    } catch (err) {
      alert(`Error updating status: ${err.message}`);
    } finally {
      const updatingIdsCopy = new Set(updatingIds);
      updatingIdsCopy.delete(`${serviceId}-${newStatus}`);
      setUpdatingIds(updatingIdsCopy);
    }
  };

  // Delete service
  const deleteService = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;

    try {
      await deleteServiceMutation({
        variables: { serviceId },
      });
      refetch();
    } catch (err) {
      alert(`Error deleting service: ${err.message}`);
    }
  };

  // Save edited service
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingService) return;

    try {
      await updateServiceMutation({
        variables: {
          serviceId: editingService.serviceId,
          name: editingService.name,
          specificDrone: editingService.specificDrone,
          experience: parseFloat(editingService.experience),
          location: editingService.location,
          price: parseFloat(editingService.price),
          description: editingService.description
        },
      });
      setEditingService(null);
      refetch();
    } catch (err) {
      alert(`Error updating service: ${err.message}`);
    }
  };

  // View service details with actions
  const viewServiceDetails = (service) => {
    setViewingService(service);
  };

  // Close service details modal
  const closeServiceDetails = () => {
    setViewingService(null);
  };

  // View description only
  const viewDescription = (service) => {
    setViewingDescription(service);
  };

  // Close description modal
  const closeDescription = () => {
    setViewingDescription(null);
  };

  // Handle edit service
  const handleEditService = (service) => {
    setEditingService({ ...service });
  };

  // Handle seller page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (tableRef.current) {
      tableRef.current.scrollLeft = 0;
    }
  };

  const statusStats = {
    all: services.length,
    approved: services.filter(s => s.status === "approved").length,
    pending: services.filter(s => s.status === "pending").length,
    rejected: services.filter(s => s.status === "rejected").length,
    Temporarily_Deactivated: services.filter(s => s.status === "Temporarily_Deactivated").length,
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return <span style={{ marginLeft: '4px', fontSize: '12px' }}>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>;
  };

  const truncateDescription = (description, length = 30) => {
    if (!description) return "No description";
    if (description.length <= length) return description;
    return description.substring(0, length) + "...";
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Calculate overall stats
  const totalValue = services.reduce((sum, s) => sum + s.price, 0);
  const pendingServices = services.filter(s => s.status === 'pending').length;

  return (
    <div className="drones-container">
      <div className="main-wrapper">
        {/* Top Navigation */}
        <div className="top-navigation">
          <div className="nav-left">
            <h1>Service Management System</h1>
            <p>Admin Dashboard • Manage Seller Services • Real-time Updates</p>
          </div>
          <div className="nav-right">
            <button
              className="btn btn-secondary"
              onClick={() => refetch()}
              disabled={loading}
            >
              {loading ? "🔄 Refreshing..." : "🔄 Refresh"}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                const headers = ["Service ID", "Seller ID", "Name", "Specific Drone", "Experience", "Location", "Price", "Status", "Description"];
                const rows = services.map(service => [
                  service.serviceId,
                  service.sellerId,
                  service.name,
                  service.specificDrone,
                  service.experience,
                  service.location,
                  service.price,
                  service.status,
                  service.description
                ]);

                let csvContent = headers.join(',') + '\n';
                rows.forEach(row => {
                  csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
                });

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `services_export_${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              📊 Export Report
            </button>
          </div>
        </div>

        {/* Plain Stats Dashboard */}
        <div className="compact-stats-container">
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Service Value</div>
              <div className="compact-stat-value">₹{totalValue.toLocaleString('en-IN')}</div>
            </div>
          </div>
          
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Services</div>
              <div className="compact-stat-value">{services.length}</div>
            </div>
          </div>
          
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Sellers</div>
              <div className="compact-stat-value">{sellerGroups.length}</div>
            </div>
          </div>
          
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Pending Approval</div>
              <div className="compact-stat-value">{pendingServices}</div>
            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="control-panel">
          <div className="panel-header">
            <h2 className="panel-title">Services Management</h2>
            <div className="sort-order-container" style={{ marginTop: '10px' }}>
              <button
                className={`status-tab ${sortOrder === "lifo" ? "active" : ""}`}
                onClick={() => setSortOrder("lifo")}
                style={{ marginRight: '10px' }}
              >
                <span style={{ marginRight: '5px' }}>📥</span> LIFO (Latest First)
              </button>
              <button
                className={`status-tab ${sortOrder === "fifo" ? "active" : ""}`}
                onClick={() => setSortOrder("fifo")}
              >
                <span style={{ marginRight: '5px' }}>📤</span> FIFO (Oldest First)
              </button>
            </div>
          </div>

          <div className="seller-selector">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search sellers by ID, email, or phone..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>

            <div className="status-tabs-container">
              {["all", "approved", "pending", "rejected", "Temporarily_Deactivated"].map((status) => (
                <button
                  key={status}
                  className={`status-tab ${selectedStatus === status ? "active" : ""}`}
                  onClick={() => {
                    setSelectedStatus(status);
                    setCurrentPage(1);
                    setProductsPage({});
                  }}
                >
                  {status === "Temporarily_Deactivated" ? "Deactivated" : status.charAt(0).toUpperCase() + status.slice(1)}
                  <span className="tab-count">{statusStats[status]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button onClick={() => refetch()}>✕</button>
          </div>
        )}

        {/* Main Content Area */}
        <div className="content-area">
          {/* Sellers Table Container */}
          <div className="table-container">
            <div className="table-header">
              <h3 className="table-title">
                All Sellers ({sellerGroups.length})
              </h3>
              <div className="table-controls">
                <div className="table-info">
                  Page {currentPage} of {totalFilteredPages || 1} • Showing {paginatedSellers.length} sellers
                </div>
              </div>
            </div>

            <div className="table-content" ref={tableRef}>
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading seller data...</p>
                </div>
              ) : sellerGroups.length === 0 ? (
                <div className="empty-state">
                  <p>No sellers found</p>
                </div>
              ) : (
                <table className="sellers-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("sellerId")}>
                        Seller ID <SortIcon column="sellerId" />
                      </th>
                      <th onClick={() => handleSort("email")}>
                        Contact Info <SortIcon column="email" />
                      </th>
                      <th onClick={() => handleSort("totalServices")}>
                        Services <SortIcon column="totalServices" />
                      </th>
                      <th onClick={() => handleSort("totalValue")}>
                        Total Value <SortIcon column="totalValue" />
                      </th>
                      <th onClick={() => handleSort("avgExperience")}>
                        Avg Experience <SortIcon column="avgExperience" />
                      </th>
                      <th>Status Breakdown</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSellers.map((seller) => {
                      const { products, currentPage: productsCurrentPage, totalPages: productsTotalPages, totalProducts } = getPaginatedProductsForSeller(seller.sellerId);
                      
                      return (
                        <React.Fragment key={seller.sellerId}>
                          <tr 
                            className={`seller-row ${expandedSeller === seller.sellerId ? 'expanded' : ''}`}
                            onClick={() => setExpandedSeller(
                              expandedSeller === seller.sellerId ? null : seller.sellerId
                            )}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div className="seller-id-cell">
                                <span className="expand-icon">
                                  {expandedSeller === seller.sellerId ? '▼' : '▶'}
                                </span>
                                <strong>Seller #{seller.sellerId}</strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-contact-cell">
                                <div className="seller-email">
                                  📧 {seller.sellerInfo?.email || "No email"}
                                </div>
                                <div className="seller-phone">
                                  📱 {seller.sellerInfo?.phoneNumber || "No phone"}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="seller-products-count">
                                <span className="count-badge">{seller.totalServices}</span>
                                <span className="count-label">services</span>
                              </div>
                            </td>
                            <td>
                              <div className="seller-value">
                                <strong className="value-amount">
                                  ₹{seller.totalValue.toLocaleString('en-IN')}
                                </strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-stock">
                                <span className="stock-count">{seller.avgExperience}</span>
                                <span className="stock-label">years avg</span>
                              </div>
                            </td>
                            <td>
                              <div className="status-breakdown">
                                <span className="status-dot approved" title={`${seller.approvedServices} approved`}>
                                  {seller.approvedServices}
                                </span>
                                <span className="status-dot pending" title={`${seller.pendingServices} pending`}>
                                  {seller.pendingServices}
                                </span>
                                <span className="status-dot rejected" title={`${seller.rejectedServices} rejected`}>
                                  {seller.rejectedServices}
                                </span>
                                <span className="status-dot deactivated" title={`${seller.deactivatedServices} deactivated`}>
                                  {seller.deactivatedServices}
                                </span>
                              </div>
                            </td>
                            <td>
                              <button
                                className="action-btn action-view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedSeller(
                                    expandedSeller === seller.sellerId ? null : seller.sellerId
                                  );
                                }}
                                title="View Services"
                              >
                                {expandedSeller === seller.sellerId ? '👁️‍🗨️' : '👁️'}
                              </button>
                            </td>
                          </tr>
                          
                          {/* Expanded Services Row */}
                          {expandedSeller === seller.sellerId && (
                            <tr className="products-row">
                              <td colSpan="7">
                                <div className="products-container">
                                  <div className="products-header">
                                    <div>
                                      <h4>Services from Seller #{seller.sellerId}</h4>
                                      <span className="products-count">
                                        {totalProducts} services • Showing {products.length} on page {productsCurrentPage} of {productsTotalPages}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {totalProducts === 0 ? (
                                    <div className="no-products">
                                      <p>No services found for the selected status filter.</p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="products-table-container">
                                        <table className="products-table">
                                          <thead>
                                            <tr>
                                              <th>Service ID</th>
                                              <th>Service Details</th>
                                              <th>Drone</th>
                                              <th>Experience</th>
                                              <th>Location</th>
                                              <th>Price</th>
                                              <th>Status</th>
                                              <th>Description</th>
                                              <th>Actions</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {products.map((service) => (
                                              <tr key={service.serviceId}>
                                                <td>
                                                  <div className="id-cell">
                                                    {service.serviceId}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-details-cell">
                                                    {service.image && (
                                                      <img src={service.image} alt={service.name} className="drone-image" />
                                                    )}
                                                    <div>
                                                      <div className="drone-name">{service.name}</div>
                                                      <div className="drone-specs">
                                                        <span className="drone-brand">{service.specificDrone}</span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-brand">{service.specificDrone}</div>
                                                </td>
                                                <td>
                                                  <div className="quantity-cell">
                                                    {service.experience} years
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-uin">{service.location}</div>
                                                </td>
                                                <td>
                                                  <div className="price-cell">
                                                    {formatPrice(service.price)}
                                                  </div>
                                                </td>
                                                <td>
                                                  <span className={`status-badge status-${service.status}`}>
                                                    {service.status === "Temporarily_Deactivated" ? "DEACTIVATED" : service.status.toUpperCase()}
                                                  </span>
                                                </td>
                                                <td>
                                                  <div>
                                                    {truncateDescription(service.description, 30)}
                                                    {service.description && service.description.length > 30 && (
                                                      <button
                                                        onClick={() => viewDescription(service)}
                                                        className="read-more-btn"
                                                      >
                                                        Read more
                                                      </button>
                                                    )}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="action-buttons">
                                                    {service.status === "pending" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateServiceStatus(service.serviceId, "approved")}
                                                          disabled={updatingIds.has(`${service.serviceId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${service.serviceId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateServiceStatus(service.serviceId, "rejected")}
                                                          disabled={updatingIds.has(`${service.serviceId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${service.serviceId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-deactivate"
                                                          onClick={() => updateServiceStatus(service.serviceId, "Temporarily_Deactivated")}
                                                          disabled={updatingIds.has(`${service.serviceId}-Temporarily_Deactivated`)}
                                                          title="Deactivate"
                                                        >
                                                          {updatingIds.has(`${service.serviceId}-Temporarily_Deactivated`) ? "..." : "⏸"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {service.status === "approved" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateServiceStatus(service.serviceId, "pending")}
                                                          disabled={updatingIds.has(`${service.serviceId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${service.serviceId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateServiceStatus(service.serviceId, "rejected")}
                                                          disabled={updatingIds.has(`${service.serviceId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${service.serviceId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-deactivate"
                                                          onClick={() => updateServiceStatus(service.serviceId, "Temporarily_Deactivated")}
                                                          disabled={updatingIds.has(`${service.serviceId}-Temporarily_Deactivated`)}
                                                          title="Deactivate"
                                                        >
                                                          {updatingIds.has(`${service.serviceId}-Temporarily_Deactivated`) ? "..." : "⏸"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {service.status === "rejected" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateServiceStatus(service.serviceId, "pending")}
                                                          disabled={updatingIds.has(`${service.serviceId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${service.serviceId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateServiceStatus(service.serviceId, "approved")}
                                                          disabled={updatingIds.has(`${service.serviceId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${service.serviceId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-deactivate"
                                                          onClick={() => updateServiceStatus(service.serviceId, "Temporarily_Deactivated")}
                                                          disabled={updatingIds.has(`${service.serviceId}-Temporarily_Deactivated`)}
                                                          title="Deactivate"
                                                        >
                                                          {updatingIds.has(`${service.serviceId}-Temporarily_Deactivated`) ? "..." : "⏸"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {service.status === "Temporarily_Deactivated" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateServiceStatus(service.serviceId, "pending")}
                                                          disabled={updatingIds.has(`${service.serviceId}-pending`)}
                                                          title="Reactivate to Pending"
                                                        >
                                                          {updatingIds.has(`${service.serviceId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateServiceStatus(service.serviceId, "approved")}
                                                          disabled={updatingIds.has(`${service.serviceId}-approved`)}
                                                          title="Reactivate to Approved"
                                                        >
                                                          {updatingIds.has(`${service.serviceId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateServiceStatus(service.serviceId, "rejected")}
                                                          disabled={updatingIds.has(`${service.serviceId}-rejected`)}
                                                          title="Move to Rejected"
                                                        >
                                                          {updatingIds.has(`${service.serviceId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                      </>
                                                    )}

                                                    <button
                                                      className="action-btn action-view"
                                                      onClick={() => viewServiceDetails(service)}
                                                      title="View Details"
                                                    >
                                                      👁
                                                    </button>

                                                  

                                                    <button
                                                      className="action-btn action-delete"
                                                      onClick={() => deleteService(service.serviceId)}
                                                      title="Delete"
                                                    >
                                                      🗑
                                                    </button>
                                                  </div>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                      
                                      {/* Products Pagination */}
                                      {productsTotalPages > 1 && (
                                        <div className="products-pagination">
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, productsCurrentPage - 1)}
                                            disabled={productsCurrentPage === 1}
                                            className="pagination-btn"
                                          >
                                            &lt; Previous
                                          </button>
                                          <div className="pagination-info">
                                            Page {productsCurrentPage} of {productsTotalPages}
                                          </div>
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, productsCurrentPage + 1)}
                                            disabled={productsCurrentPage === productsTotalPages}
                                            className="pagination-btn"
                                          >
                                            Next &gt;
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls for Sellers */}
            {totalFilteredPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalFilteredPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    className={currentPage === num ? "active" : ""}
                    onClick={() => handlePageChange(num)}
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalFilteredPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Service Details Modal with Actions */}
      {viewingService && (
        <div className="modal-overlay" onClick={closeServiceDetails}>
          <div className="modal-content drone-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Service Details</h2>
              <button className="close-btn" onClick={closeServiceDetails}>×</button>
            </div>
            <div className="modal-body">
              <div className="drone-details-grid">
                <div className="drone-image-section">
                  {viewingService.image ? (
                    <img 
                      src={viewingService.image} 
                      alt={viewingService.name} 
                      className="drone-detail-image"
                    />
                  ) : (
                    <div className="no-image-placeholder">No Image</div>
                  )}
                </div>
                
                <div className="drone-info-section">
                  <div className="info-row">
                    <div className="info-label">Service ID</div>
                    <div className="info-value">{viewingService.serviceId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Name</div>
                    <div className="info-value">{viewingService.name}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Specific Drone</div>
                    <div className="info-value">{viewingService.specificDrone}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Experience</div>
                    <div className="info-value">{viewingService.experience} years</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Location</div>
                    <div className="info-value">{viewingService.location}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Price</div>
                    <div className="info-value price-value">{formatPrice(viewingService.price)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Status</div>
                    <div className="info-value">
                      <span className={`status-badge status-${viewingService.status}`}>
                        {viewingService.status === "Temporarily_Deactivated" ? "DEACTIVATED" : viewingService.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller ID</div>
                    <div className="info-value">{viewingService.sellerId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Email</div>
                    <div className="info-value">{viewingService.sellerInfo?.email || "N/A"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Phone</div>
                    <div className="info-value">{viewingService.sellerInfo?.phoneNumber || "N/A"}</div>
                  </div>
                </div>
                
                <div className="drone-description-section">
                  <div className="info-label">Description</div>
                  <div className="description-content">
                    {viewingService.description || 'No description available'}
                  </div>
                </div>
                
                {/* Actions in Details Modal */}
                <div className="drone-actions-section">
                  <div className="info-label">Quick Actions</div>
                  <div className="action-buttons-grid">
                    {viewingService.status === "pending" && (
                      <>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateServiceStatus(viewingService.serviceId, "approved");
                            closeServiceDetails();
                          }}
                          disabled={updatingIds.has(`${viewingService.serviceId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingService.serviceId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateServiceStatus(viewingService.serviceId, "rejected");
                            closeServiceDetails();
                          }}
                          disabled={updatingIds.has(`${viewingService.serviceId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingService.serviceId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                        <button
                          className="action-btn-large action-deactivate"
                          onClick={() => {
                            updateServiceStatus(viewingService.serviceId, "Temporarily_Deactivated");
                            closeServiceDetails();
                          }}
                          disabled={updatingIds.has(`${viewingService.serviceId}-Temporarily_Deactivated`)}
                          title="Deactivate"
                        >
                          {updatingIds.has(`${viewingService.serviceId}-Temporarily_Deactivated`) ? "..." : "⏸ Deactivate"}
                        </button>
                      </>
                    )}

                    {viewingService.status === "approved" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateServiceStatus(viewingService.serviceId, "pending");
                            closeServiceDetails();
                          }}
                          disabled={updatingIds.has(`${viewingService.serviceId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingService.serviceId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateServiceStatus(viewingService.serviceId, "rejected");
                            closeServiceDetails();
                          }}
                          disabled={updatingIds.has(`${viewingService.serviceId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingService.serviceId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                        <button
                          className="action-btn-large action-deactivate"
                          onClick={() => {
                            updateServiceStatus(viewingService.serviceId, "Temporarily_Deactivated");
                            closeServiceDetails();
                          }}
                          disabled={updatingIds.has(`${viewingService.serviceId}-Temporarily_Deactivated`)}
                          title="Deactivate"
                        >
                          {updatingIds.has(`${viewingService.serviceId}-Temporarily_Deactivated`) ? "..." : "⏸ Deactivate"}
                        </button>
                      </>
                    )}

                    {viewingService.status === "rejected" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateServiceStatus(viewingService.serviceId, "pending");
                            closeServiceDetails();
                          }}
                          disabled={updatingIds.has(`${viewingService.serviceId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingService.serviceId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateServiceStatus(viewingService.serviceId, "approved");
                            closeServiceDetails();
                          }}
                          disabled={updatingIds.has(`${viewingService.serviceId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingService.serviceId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-deactivate"
                          onClick={() => {
                            updateServiceStatus(viewingService.serviceId, "Temporarily_Deactivated");
                            closeServiceDetails();
                          }}
                          disabled={updatingIds.has(`${viewingService.serviceId}-Temporarily_Deactivated`)}
                          title="Deactivate"
                        >
                          {updatingIds.has(`${viewingService.serviceId}-Temporarily_Deactivated`) ? "..." : "⏸ Deactivate"}
                        </button>
                      </>
                    )}

                    {viewingService.status === "Temporarily_Deactivated" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateServiceStatus(viewingService.serviceId, "pending");
                            closeServiceDetails();
                          }}
                          disabled={updatingIds.has(`${viewingService.serviceId}-pending`)}
                          title="Reactivate to Pending"
                        >
                          {updatingIds.has(`${viewingService.serviceId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateServiceStatus(viewingService.serviceId, "approved");
                            closeServiceDetails();
                          }}
                          disabled={updatingIds.has(`${viewingService.serviceId}-approved`)}
                          title="Reactivate to Approved"
                        >
                          {updatingIds.has(`${viewingService.serviceId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateServiceStatus(viewingService.serviceId, "rejected");
                            closeServiceDetails();
                          }}
                          disabled={updatingIds.has(`${viewingService.serviceId}-rejected`)}
                          title="Move to Rejected"
                        >
                          {updatingIds.has(`${viewingService.serviceId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                      </>
                    )}

                

                    <button
                      className="action-btn-large action-delete"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this service?")) {
                          deleteService(viewingService.serviceId);
                          closeServiceDetails();
                        }
                      }}
                      title="Delete"
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {viewingDescription && (
        <div className="modal-overlay" onClick={closeDescription}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Description</h2>
              <button className="close-btn" onClick={closeDescription}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap' }}>
                {viewingDescription.description}
              </p>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
}

export default Services;